import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Users, Sparkles, X } from 'lucide-react';
import { trackEvent } from '../lib/track';
import { useT } from '../i18n';

// Special-request banner for the kindergarten-graduation book series: one shared
// story for the whole class where every child gets their own copy as the hero
// (their photo + name), with the rest of the class woven into the story.
// Clicking opens a small popup (count + deadline); on submit we hand the buyer
// off to WhatsApp with a pre-filled special-request message. Hardcover only,
// 200₪ per book.
const WHATSAPP_NUMBER = '972505020178';
const REGULAR_PRICE = 250;   // single-book hardcover price
const SERIES_PRICE = 200;    // per-book price inside a full-class series — needs MIN_BOOKS+
const SAVINGS_PER_BOOK = REGULAR_PRICE - SERIES_PRICE;
const TYPICAL_CLASS = 50;    // reference series size used in the headline savings
const MIN_BOOKS = 10;        // series price (200₪) applies from this many books up

// Visual hook: a fanned SERIES of dedicated 6-year-old hero covers (generated
// just for this banner, so the shared catalog samples stay untouched). Each is
// a different kindergarten-age child — the image reads "we make one book per
// kid, for the whole class." The front cover (a hero kid with his classmates
// cheering behind him) carries the "the rest of the class is part of the story"
// idea; the others stay clearly legible, only lightly faded toward the edges.
// Order is back-to-front so the centred hero stacks on top.
const HERO_FAN = [
  { src: '/samples/grad-hero-3.png', cls: 'grad-fan-card grad-fan--l2' },
  { src: '/samples/grad-hero-6.png', cls: 'grad-fan-card grad-fan--r2' },
  { src: '/samples/grad-hero-1.png', cls: 'grad-fan-card grad-fan--l1' },
  { src: '/samples/grad-hero-2.png', cls: 'grad-fan-card grad-fan--r1' },
  { src: '/samples/grad-hero-5.png', cls: 'grad-fan-card grad-fan--front' },
];

export default function GraduationSeriesBanner() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState('');
  const [deadline, setDeadline] = useState('');

  // Count a banner engagement the same way the deal PromoBanner does — a
  // promo_click event — so it rolls into the admin Stats "نقرات بانر العرض".
  // Tagged as the new-product promo (the kindergarten graduation series).
  const openPopup = () => {
    trackEvent('promo_click', { slug: 'new-product-promo', name: t.gradTrackName });
    setOpen(true);
  };

  const n = parseInt(count, 10);
  const hasCount = Number.isFinite(n) && n > 0;
  // The 200₪ series price only applies from MIN_BOOKS up; below that we don't
  // promise it — we ask the buyer for details and arrange a price by hand.
  const meetsMin = hasCount && n >= MIN_BOOKS;
  const belowMin = hasCount && n < MIN_BOOKS;
  const total = meetsMin ? n * SERIES_PRICE : null;
  const regularTotal = meetsMin ? n * REGULAR_PRICE : null;
  const savings = meetsMin ? n * SAVINGS_PER_BOOK : null;

  const submit = (e) => {
    e.preventDefault();
    const lines = [
      t.gradWhatsAppLine1,
      t.gradWhatsAppLine2,
      '',
      t.gradWhatsAppCount.replace('{n}', n),
      t.gradWhatsAppDeadline.replace('{deadline}', deadline),
    ];
    // Under the 10-book minimum the 200₪ price doesn't apply — flag it so the
    // team follows up with a tailored quote instead of the series price.
    if (belowMin) lines.push(t.gradWhatsAppBelowMin);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join('\n'))}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  return (
    <>
      <button type="button" className="grad-banner" onClick={openPopup}>
        <span className="grad-banner-confetti" aria-hidden="true" />
        <span className="grad-banner-collage" aria-hidden="true">
          {HERO_FAN.map((h) => (
            <span key={h.src} className={h.cls} style={{ backgroundImage: `url(${h.src})` }} />
          ))}
          <span className="grad-fan-badge">🎓</span>
        </span>
        <span className="grad-banner-body">
          <span className="grad-banner-badge"><Sparkles size={13} aria-hidden="true" /> {t.gradBannerBadge}</span>
          <span className="grad-banner-title">{t.gradBannerTitle}</span>
          <span className="grad-banner-text">
            {t.gradBannerText}
          </span>
          <span className="grad-banner-pricing">
            <span className="grad-banner-price">
              <span className="grad-price-old">{REGULAR_PRICE}₪</span>
              <span className="grad-price-new">{SERIES_PRICE}₪</span>
              <span className="grad-price-unit">{t.gradBannerPriceUnit}</span>
            </span>
            <span className="grad-banner-save">{t.gradBannerSave.replace('{amount}', SAVINGS_PER_BOOK)}</span>
          </span>
          <span className="grad-banner-cta">{t.gradBannerCta}</span>
        </span>
      </button>

      {open && createPortal((
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal-card grad-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close" aria-label={t.gradModalClose} onClick={() => setOpen(false)}>
              <X size={22} />
            </button>
            <h3 className="modal-title">{t.gradModalTitle}</h3>
            <p className="modal-text">
              {t.gradModalText}
            </p>

            <form onSubmit={submit}>
              <label className="field">
                <span className="field-label">{t.gradModalCountLabel} <span className="field-required">*</span></span>
                <input
                  type="number"
                  min="1"
                  inputMode="numeric"
                  placeholder={t.gradModalCountPlaceholder}
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  required
                />
                <span className="field-helper">{t.gradModalCountHelper}</span>
              </label>

              <label className="field">
                <span className="field-label">{t.gradModalDeadlineLabel} <span className="field-required">*</span></span>
                <input
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  required
                />
              </label>

              <div className="grad-modal-price">
                <div className="grad-modal-price-row">
                  <Users size={16} aria-hidden="true" />
                  <span>
                    {t.gradModalPriceRow} <s>{REGULAR_PRICE}₪</s>{' '}
                    <strong>{SERIES_PRICE}₪</strong> {t.gradModalPricePerBook} <strong>{t.gradModalPriceMinBooks}</strong> {t.gradModalPriceOrMore}
                  </span>
                </div>
                {meetsMin && (
                  <div className="grad-modal-total">
                    <span>{t.gradModalTotal.replace('{n}', n)} <strong>{total}₪</strong> <s>{regularTotal}₪</s></span>
                    <span className="grad-modal-savings">{t.gradModalSavings.replace('{amount}', savings)}</span>
                  </div>
                )}
                {belowMin && (
                  <div className="grad-modal-warn">
                    {t.gradModalBelowMin}
                  </div>
                )}
                {!hasCount && (
                  <div className="grad-modal-hint">
                    {t.gradModalHint.replace('{count}', TYPICAL_CLASS).replace('{amount}', TYPICAL_CLASS * SAVINGS_PER_BOOK)}
                  </div>
                )}
              </div>

              <button type="submit" className="submit-btn">{t.gradModalSubmit}</button>
            </form>
          </div>
        </div>
      ), document.body)}
    </>
  );
}
